/**
 * Activity / Dashboard - Phase 3 wiring.
 *
 * Live data sources:
 *   GET /v1/activity/events?limit=1000          → recent events for QPS + top-actor aggregates
 *   GET /v1/activity/ueba/findings              → "Recent alerts" counter
 *   GET /v1/activity/early-warning              → "P-Wave fires (24h)" counter
 *
 * Both the QPS time series and the top-actors panel are computed
 * client-side from the most recent events the server returns. This is
 * the right shape for a PoC: no new backend aggregation route to build,
 * and the server already supports limit + since filters.
 *
 * As the activity volume grows, this client-side approach will hit its
 * limits and we should move to ClickHouse aggregations (Phase 3 part 2 -
 * `seed-activity-demo.ts` + dedicated `/aggregates/qps` and
 * `/aggregates/top-actors` endpoints). Documented in tracker §12 Entry #45.
 */

import { useEffect, useMemo, useState } from 'react';
import { Activity as ActivityIcon, TrendingUp, Waves, Radio, Timer, AlertTriangle, Loader2 } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { KpiCard } from '@/components/viz/KpiCard';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { ErrorState, LoadingState } from '@foctta/ui-shell';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip as RTooltip, CartesianGrid } from 'recharts';
import { motion } from 'motion/react';
import {
  listActivityEvents,
  listUebaFindings,
  listEarlyWarning,
  type ActivityEvent,
} from '@/lib/api';

interface ActorRow {
  actor: string;
  queries: number;
  blocked: number;
  sev: 'high' | 'medium' | 'low';
}

function bucketEventsByMinute(events: ActivityEvent[], bucketMinutes = 1, totalMinutes = 60): Array<{ t: string; qps: number; blocked: number }> {
  const buckets = new Array(totalMinutes).fill(0).map(() => ({ qps: 0, blocked: 0 }));
  const nowMs = Date.now();
  const windowMs = totalMinutes * bucketMinutes * 60_000;
  for (const e of events) {
    const ts = new Date(e.occurredAt).getTime();
    const ageMs = nowMs - ts;
    if (ageMs < 0 || ageMs > windowMs) continue;
    const idx = Math.min(totalMinutes - 1, Math.max(0, Math.floor((windowMs - ageMs) / (bucketMinutes * 60_000))));
    buckets[idx]!.qps += 1;
    if (e.decision === 'block') buckets[idx]!.blocked += 1;
  }
  return buckets.map((b, i) => ({ t: `${(totalMinutes - i - 1) * bucketMinutes}m`, qps: b.qps, blocked: b.blocked }));
}

function aggregateTopActors(events: ActivityEvent[], topN = 5): ActorRow[] {
  const byActor: Record<string, { queries: number; blocked: number }> = {};
  for (const e of events) {
    const a = e.actorUserId || 'unknown';
    byActor[a] = byActor[a] ?? { queries: 0, blocked: 0 };
    byActor[a].queries += 1;
    if (e.decision === 'block') byActor[a].blocked += 1;
  }
  return Object.entries(byActor)
    .map(([actor, v]) => {
      const ratio = v.queries > 0 ? v.blocked / v.queries : 0;
      const sev: ActorRow['sev'] = ratio > 0.05 ? 'high' : ratio > 0.01 ? 'medium' : 'low';
      return { actor, queries: v.queries, blocked: v.blocked, sev };
    })
    .sort((a, b) => b.queries - a.queries)
    .slice(0, topN);
}

export function ActivityDashboard() {
  const [events, setEvents] = useState<ActivityEvent[]>([]);
  const [recentAlerts, setRecentAlerts] = useState(0);
  const [pwaveFires, setPwaveFires] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = (signal?: AbortSignal) =>
    Promise.allSettled([
      listActivityEvents({ limit: 1000 }),
      listUebaFindings(),
      listEarlyWarning(),
    ]).then(([evRes, uebaRes, ewRes]) => {
      if (signal?.aborted) return;
      const evs = evRes.status === 'fulfilled' ? evRes.value.events : [];
      const ueba = uebaRes.status === 'fulfilled' ? uebaRes.value.findings.length : 0;
      const cutoff24h = Date.now() - 24 * 3600 * 1000;
      const ew = ewRes.status === 'fulfilled'
        ? ewRes.value.indicators.filter((i) => new Date(i.firedAt).getTime() >= cutoff24h).length
        : 0;
      const errs: string[] = [];
      if (evRes.status === 'rejected') errs.push(`events: ${(evRes.reason as Error)?.message ?? 'failed'}`);
      if (uebaRes.status === 'rejected') errs.push(`ueba: ${(uebaRes.reason as Error)?.message ?? 'failed'}`);
      if (ewRes.status === 'rejected') errs.push(`early-warning: ${(ewRes.reason as Error)?.message ?? 'failed'}`);
      setEvents(evs);
      setRecentAlerts(ueba);
      setPwaveFires(ew);
      setError(errs.length > 0 ? errs.join(' · ') : null);
      setLoading(false);
    });

  useEffect(() => {
    const ctrl = new AbortController();
    refresh(ctrl.signal);
    return () => ctrl.abort();
  }, []);

  // KPI calculations.
  const cutoffHr = Date.now() - 3600 * 1000;
  const cutoff24h = Date.now() - 24 * 3600 * 1000;
  const queriesLastHour = events.filter((e) => new Date(e.occurredAt).getTime() >= cutoffHr).length;
  const blocked24h = events.filter((e) => e.decision === 'block' && new Date(e.occurredAt).getTime() >= cutoff24h).length;

  const qpsSeries = useMemo(() => bucketEventsByMinute(events), [events]);
  const topActors = useMemo(() => aggregateTopActors(events), [events]);
  const topQuery = Math.max(1, ...topActors.map((a) => a.queries));

  return (
    <PageContainer>
      <PageHeader
        title="Activity"
        subtitle="Database Activity Monitoring with statement hashing, UEBA baselines, Submarine Sonar, and the only Consent-Aware Query Firewall in India."
        badges={[
          { label: 'DAM', variant: 'glass' },
          ...(loading
            ? [{ label: 'Loading', variant: 'glass' as const }]
            : error
              ? [{ label: 'Degraded', variant: 'warning' as const }]
              : [{ label: 'Live', variant: 'success' as const }]),
        ]}
        actions={<Button size="sm" asChild><Link to="/activity/stream">Open live stream</Link></Button>}
      />

      {loading && <LoadingState caption="Loading Activity dashboard…" />}
      {error && !loading && (
        <ErrorState
          title="Some Activity data unavailable"
          detail={error}
          onRetry={() => { setError(null); setLoading(true); refresh(); }}
        />
      )}

      {!loading && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <KpiCard
              label="Queries / hour"
              value={queriesLastHour}
              icon={ActivityIcon}
              hint="from last 1h of events"
            />
            <KpiCard
              label="Queries blocked (24h)"
              value={blocked24h}
              icon={AlertTriangle}
              glow={blocked24h > 0 ? 'medium' : 'low'}
              hint="decision = block"
            />
            <KpiCard
              label="UEBA alerts"
              value={recentAlerts}
              icon={TrendingUp}
              glow={recentAlerts > 0 ? 'high' : 'low'}
              hint="open findings"
            />
            <KpiCard
              label="P-Wave fires (24h)"
              value={pwaveFires}
              icon={Radio}
              glow={pwaveFires > 0 ? 'high' : 'low'}
              hint="early-warning indicators"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
            <GlassCard className="lg:col-span-2 p-6">
              <CardHeader className="p-0 mb-4 flex-row items-center justify-between">
                <div>
                  <CardTitle>Live query throughput · last 60 minutes</CardTitle>
                  <CardDescription>QPS vs blocked QPS, by minute (computed from last 1000 events)</CardDescription>
                </div>
                <Badge variant="glass" className="gap-1.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-success animate-pulse" />
                  Live
                </Badge>
              </CardHeader>
              {events.length === 0 ? (
                <div className="text-xs text-muted-foreground py-12 text-center">
                  No activity events on file yet. Connect a database, run a scan, or wait for the live stream to start populating.
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <AreaChart data={qpsSeries}>
                    <defs>
                      <linearGradient id="g-qps" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="hsl(var(--chart-2))" stopOpacity={0.6} />
                        <stop offset="100%" stopColor="hsl(var(--chart-2))" stopOpacity={0.05} />
                      </linearGradient>
                      <linearGradient id="g-blk" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="hsl(var(--sev-critical))" stopOpacity={0.85} />
                        <stop offset="100%" stopColor="hsl(var(--sev-critical))" stopOpacity={0.1} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                    <XAxis dataKey="t" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }} stroke="hsl(var(--border))" />
                    <YAxis yAxisId="l" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }} stroke="hsl(var(--border))" />
                    <YAxis yAxisId="r" orientation="right" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }} stroke="hsl(var(--border))" />
                    <RTooltip contentStyle={{ background: 'hsl(var(--popover))', border: '1px solid hsl(var(--border))', borderRadius: 8 }} />
                    <Area yAxisId="l" type="monotone" dataKey="qps" stroke="hsl(var(--chart-2))" fill="url(#g-qps)" strokeWidth={2} />
                    <Area yAxisId="r" type="monotone" dataKey="blocked" stroke="hsl(var(--sev-critical))" fill="url(#g-blk)" strokeWidth={1.5} />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </GlassCard>

            <GlassCard className="p-6">
              <CardHeader className="p-0 mb-3">
                <CardTitle>Top actors</CardTitle>
                <CardDescription>By query count in the recent window. Severity by block ratio.</CardDescription>
              </CardHeader>
              {topActors.length === 0 ? (
                <div className="text-xs text-muted-foreground py-6 text-center">
                  No actors observed yet.
                </div>
              ) : (
                <div className="space-y-2.5">
                  {topActors.map((a, i) => (
                    <motion.div
                      key={a.actor}
                      initial={{ opacity: 0, x: -6 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.05 }}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <code className="text-[11px] font-mono truncate max-w-[60%]">{a.actor}</code>
                        <span className="text-xs tabular">{a.queries.toLocaleString('en-IN')}</span>
                      </div>
                      <div className="h-1 bg-muted rounded-full overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${(a.queries / topQuery) * 100}%` }}
                          transition={{ delay: 0.2 + i * 0.05, duration: 0.7 }}
                          className={`h-full bg-gradient-to-r ${a.sev === 'high' ? 'from-sev-critical to-sev-high' : a.sev === 'medium' ? 'from-sev-medium to-sev-low' : 'from-chart-2 to-chart-6'}`}
                        />
                      </div>
                      {a.blocked > 0 && (
                        <div className="text-[10px] text-sev-critical mt-1">{a.blocked} blocked</div>
                      )}
                    </motion.div>
                  ))}
                </div>
              )}
            </GlassCard>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <QuickLink to="/activity/stream" icon={ActivityIcon} label="Live Stream" />
            <QuickLink to="/activity/ueba" icon={TrendingUp} label="UEBA" />
            <QuickLink to="/activity/sonar" icon={Waves} label="Sonar" />
            <QuickLink to="/activity/p-wave" icon={Radio} label="P-Wave" />
            <QuickLink to="/activity/breach-readiness" icon={Timer} label="Breach 6h" />
          </div>
        </>
      )}
    </PageContainer>
  );
}

function QuickLink({ to, icon: Icon, label }: { to: string; icon: React.ComponentType<{ className?: string }>; label: string }) {
  return (
    <Link to={to}>
      <GlassCard className="p-4 flex flex-col items-center gap-2 lift cursor-pointer">
        <Icon className="h-5 w-5 text-primary" />
        <span className="text-xs font-medium text-center">{label}</span>
      </GlassCard>
    </Link>
  );
}
